import {Product} from "../../../../models/product";
import {DELIVERY_EXPENSES, FREE_DELIVERY_THRESHOLD} from "../../../../services/util/constants";

export interface FaqEntry {
    question: string;
    answer: string;
}

/**
 * Questions people ask Google and AI assistants about any product we sell - where to buy it,
 * what it costs, delivery and payment - answered from the product's own data, so every
 * product has them and the prices in them never go stale. Product-specific questions
 * (dosage, ingredients) come from the admin content and are merged in by the caller.
 */
export function buildProductFaq(product: Product): FaqEntry[] {
    const [localName, latinName] = product.name.split('|').map(part => part.trim());
    const fullName = latinName && !localName.includes(latinName) ? `${localName} (${latinName})` : localName;
    const price = Math.round(product.price);
    const freeDelivery = product.price >= FREE_DELIVERY_THRESHOLD;

    const faq: FaqEntry[] = [
        {
            question: `Каде да купам ${fullName} во Македонија?`,
            answer: `На aloeveragel.mk. Нарачувате без регистрација, со достава низ цела Македонија и плаќање при достава.`
        },
        {
            question: `Колку чини ${localName}?`,
            answer: product.originalPrice && product.originalPrice > product.price
                ? `${price} ден. (намалено од ${Math.round(product.originalPrice)} ден.)`
                : `${price} ден.`
        },
        {
            question: `Колку чини доставата за ${localName}?`,
            answer: freeDelivery
                ? `Доставата е бесплатна, бидејќи цената е над ${FREE_DELIVERY_THRESHOLD} ден.`
                : `${DELIVERY_EXPENSES} ден. низ цела Македонија. За нарачки над ${FREE_DELIVERY_THRESHOLD} ден. доставата е бесплатна.`
        },
        {
            question: `Како се плаќа ${localName}?`,
            answer: `Во готово при достава, кога ќе ја добиете пратката. Не е потребна картичка.`
        }
    ];

    if (product.category === 'ALOE_VERA_PIJALOCI') {
        faq.push({
            question: `Како се чува ${localName} по отворање?`,
            answer: `Затворен, во фрижидер. Протресете го пред секоја употреба.`
        });
    }

    return faq;
}

/** What a question is about - two questions on the same topic would repeat each other. */
const TOPICS: RegExp[] = [/каде да купам/, /чува/, /достав/, /плаќ/, /колку чини|цена/];

/** Content questions first; a generated one is left out when the content already asks about its topic. */
export function mergeFaq(fromContent: FaqEntry[], generated: FaqEntry[]): FaqEntry[] {
    const asked = fromContent.map(entry => entry.question.toLowerCase());
    const alreadyAsked = (question: string) => {
        const topic = TOPICS.find(pattern => pattern.test(question.toLowerCase()));
        return !!topic && asked.some(existing => topic.test(existing));
    };
    return [...fromContent, ...generated.filter(entry => !alreadyAsked(entry.question))];
}
